import React, { useContext, useEffect, useState } from 'react'
import Table, {
  TableBody,
  TableData,
  TableHeader,
  TableHeaderItem,
  TableRow,
} from 'components/Table'
import { useTranslation } from 'react-i18next'
import './BroadSignCreationMediaTable.scss'
import Checkbox from '../../../../components/Form/Checkbox'
import Button, { ButtonSize, ButtonTheme } from '../../../../components/Form/Button'
import {
  BroadSignMediaTreeAgglomeration,
  BroadSignMediaTreeBuilding,
  MediaTreeNodeType,
} from '../../../../api/broadSign/models/broad-sign-media-tree.model'
import BroadSignService from '../../services/broad-sign.service'
import { AppContext } from '../../../../contexts/AppContext'
import { BroadSignCreationAcceptanceModel } from '../../../../api/broadSign/models/broad-sign-creation-acceptance.model'

const EMPTY_VALUE = '-'

interface GalleryRow {
  key: string
  id: string
  name: string
  cityName: string
  mediumIds: string[]
}

// a gallery is a Media::Building node, its media are its own children
const mediumNodes = (gallery: BroadSignMediaTreeBuilding) =>
  (gallery.children ?? []).filter(medium => medium.type === MediaTreeNodeType.MEDIUM)

// a gallery belongs to the acceptances table as soon as at least one of its medium nodes carries the creation
const hasUsedMedium = (gallery: BroadSignMediaTreeBuilding) =>
  mediumNodes(gallery).some(medium => !!medium.used)

// a gallery is free only when none of its medium nodes carries the creation
const allMediumsFree = (gallery: BroadSignMediaTreeBuilding) =>
  mediumNodes(gallery).every(medium => !medium.used)

const galleries = (mediaTree: BroadSignMediaTreeAgglomeration[]) =>
  mediaTree.flatMap(agglomeration =>
    agglomeration.children
      .filter(city => city.type === MediaTreeNodeType.CITY)
      .flatMap(city => city.children.filter(gallery => gallery.type === MediaTreeNodeType.BUILDING))
  )

// the mutation replaces the whole selection instead of applying a diff, so the payload carries every
// medium of every gallery and only the picked ones change their flag
const treeMediums = (mediaTree: BroadSignMediaTreeAgglomeration[]) =>
  galleries(mediaTree).flatMap(gallery => mediumNodes(gallery))

// the tree is Agglomeration → City → Building → Medium, the galleries are the building nodes
const galleryRows = (
  mediaTree: BroadSignMediaTreeAgglomeration[],
  matches: (gallery: BroadSignMediaTreeBuilding) => boolean
): GalleryRow[] =>
  mediaTree.flatMap(agglomeration =>
    agglomeration.children
      .filter(city => city.type === MediaTreeNodeType.CITY)
      .flatMap(city =>
        city.children
          .filter(gallery => gallery.type === MediaTreeNodeType.BUILDING)
          .filter(matches)
          .map(gallery => ({
            // buildings unknown to the tree builder all share the same id
            key: `${city.id}-${gallery.id}`,
            id: gallery.id,
            name: gallery.name,
            cityName: city.name,
            // every medium node of the gallery, the payload lists them all
            mediumIds: mediumNodes(gallery).map(medium => medium.id),
          }))
      )
  )

const BroadSignCreationMediaTable: React.FC<{
  mediaTree: BroadSignMediaTreeAgglomeration[]
  acceptances: BroadSignCreationAcceptanceModel[]
  creationId: string
  creationName: string
}> = ({ mediaTree, acceptances, creationId, creationName }) => {
  const { t } = useTranslation()
  const { addNotification } = useContext(AppContext)

  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [selectedFreeKeys, setSelectedFreeKeys] = useState<string[]>([])

  const galleries = galleryRows(mediaTree, hasUsedMedium)
  const freeGalleries = galleryRows(mediaTree, allMediumsFree)

  useEffect(() => {
    setSelectedKeys([])
    setSelectedFreeKeys([])
  }, [mediaTree])

  const allSelected = !!galleries.length && selectedKeys.length === galleries.length

  const toggleGallery = (key: string) =>
    setSelectedKeys(keys =>
      keys.includes(key) ? keys.filter(selected => selected !== key) : [...keys, key]
    )

  const toggleAllGalleries = () =>
    setSelectedKeys(allSelected ? [] : galleries.map(gallery => gallery.key))

  const allFreeSelected = !!freeGalleries.length && selectedFreeKeys.length === freeGalleries.length

  const toggleFreeGallery = (key: string) =>
    setSelectedFreeKeys(keys =>
      keys.includes(key) ? keys.filter(selected => selected !== key) : [...keys, key]
    )

  const toggleAllFreeGalleries = () =>
    setSelectedFreeKeys(allFreeSelected ? [] : freeGalleries.map(gallery => gallery.key))

  // the whole creation goes to acceptance, the galleries it covers are the ones already assigned
  const onSendToAcceptances = () =>
    void BroadSignService.sendCreationToAcceptance(creationId, addNotification, t)

  // every medium of the picked galleries flips at once, that is what moves them between the tables.
  // the media of the galleries left alone go out with the flag they already carry
  const assignMedia = (rows: GalleryRow[], keys: string[], used: boolean) => {
    const picked = new Set(
      rows.filter(gallery => keys.includes(gallery.key)).flatMap(gallery => gallery.mediumIds)
    )

    if (!picked.size) {
      return
    }

    const media = treeMediums(mediaTree).map(medium => ({
      id: medium.id,
      used: picked.has(medium.id) ? used : !!medium.used,
    }))

    void BroadSignService.assignCreationMedia(creationId, media, addNotification, t)
  }

  const onUnassignFromAcceptances = () => assignMedia(galleries, selectedKeys, false)

  const onAssignToAcceptances = () => assignMedia(freeGalleries, selectedFreeKeys, true)

  const noData = (
    <div className='BroadSignCreationMediaTable__no-data'>
      <p>{t('common.noData')}</p>
    </div>
  )

  return (
    <div className='BroadSignCreationMediaTable'>
      <div className='BroadSignCreationMediaTable__table'>
        <div className='BroadSignCreationMediaTable__title'>
          <span>{t('broadSign.acceptances')}</span>

          {!!selectedKeys.length && (
            <Button
              theme={ButtonTheme.RED_OUTLINE}
              size={ButtonSize.SMALL}
              onClick={onUnassignFromAcceptances}
              dataCy='broadsign-unassign-from-acceptances'
            >
              {t('broadSign.unassignFromAcceptances')}
            </Button>
          )}
        </div>

        {galleries.length ? (
          <Table className='CommonTable'>
            <TableHeader sticky>
              <>
                <TableHeaderItem className='CommonTable__header-item'>
                  <Checkbox
                    id='broadsign-acceptances-select-all'
                    checked={allSelected}
                    onChange={toggleAllGalleries}
                    dataCy='broadsign-acceptances-select-all'
                  />
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.name')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.city')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.creationName')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.screenNumber')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.category')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.competitors')}
                </TableHeaderItem>
              </>
            </TableHeader>

            <TableBody>
              {galleries.map(gallery => (
                <TableRow
                  key={gallery.key}
                  className='CommonTable__data-row'
                  onClick={() => toggleGallery(gallery.key)}
                >
                  <>
                    <TableData className='CommonTable__data-item'>
                      {/* the checkbox toggles on its own, the row click must not toggle it back */}
                      <div onClick={event => event.stopPropagation()}>
                        <Checkbox
                          id={`broadsign-acceptances-${gallery.key}`}
                          checked={selectedKeys.includes(gallery.key)}
                          onChange={() => toggleGallery(gallery.key)}
                        />
                      </div>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{gallery.name}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{gallery.cityName}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{creationName}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{EMPTY_VALUE}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{EMPTY_VALUE}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{EMPTY_VALUE}</span>
                    </TableData>
                  </>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          noData
        )}
      </div>

      <div className='BroadSignCreationMediaTable__actions'>
        <Button
          theme={ButtonTheme.GREEN}
          onClick={onSendToAcceptances}
          disabled={!galleries.length}
          dataCy='broadsign-send-to-acceptances'
        >
          {t('broadSign.sendToAcceptances')}
        </Button>
      </div>

      <div className='BroadSignCreationMediaTable__table BroadSignCreationMediaTable__table--free'>
        <div className='BroadSignCreationMediaTable__title BroadSignCreationMediaTable__title--free'>
          <span>{t('broadSign.competitors')}</span>

          {!!selectedFreeKeys.length && (
            <Button
              theme={ButtonTheme.GREEN}
              size={ButtonSize.SMALL}
              onClick={onAssignToAcceptances}
              dataCy='broadsign-assign-to-acceptances'
            >
              {t('broadSign.assignToAcceptances')}
            </Button>
          )}
        </div>

        {freeGalleries.length ? (
          <Table className='CommonTable'>
            <TableHeader sticky>
              <>
                <TableHeaderItem className='CommonTable__header-item'>
                  <Checkbox
                    id='broadsign-free-media-select-all'
                    checked={allFreeSelected}
                    onChange={toggleAllFreeGalleries}
                    dataCy='broadsign-free-media-select-all'
                  />
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.name')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.city')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.creationName')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.category')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.competitors')}
                </TableHeaderItem>
              </>
            </TableHeader>

            <TableBody>
              {freeGalleries.map(gallery => (
                <TableRow
                  key={gallery.key}
                  className='CommonTable__data-row'
                  onClick={() => toggleFreeGallery(gallery.key)}
                >
                  <>
                    <TableData className='CommonTable__data-item'>
                      <div onClick={event => event.stopPropagation()}>
                        <Checkbox
                          id={`broadsign-free-media-${gallery.key}`}
                          checked={selectedFreeKeys.includes(gallery.key)}
                          onChange={() => toggleFreeGallery(gallery.key)}
                        />
                      </div>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{gallery.name}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{gallery.cityName}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{creationName}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{EMPTY_VALUE}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{EMPTY_VALUE}</span>
                    </TableData>
                  </>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          noData
        )}
      </div>

      <div className='BroadSignCreationMediaTable__table BroadSignCreationMediaTable__table--responses'>
        <div className='BroadSignCreationMediaTable__title BroadSignCreationMediaTable__title--responses'>
          <span>{t('broadSign.galleryResponses')}</span>
        </div>

        {acceptances.length ? (
          <Table className='CommonTable'>
            <TableHeader sticky>
              <>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.galleryName')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.city')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.decision')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.rejectionReason')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.category')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.competitors')}
                </TableHeaderItem>
              </>
            </TableHeader>

            <TableBody>
              {acceptances.map(acceptance => (
                <TableRow
                  key={acceptance.id}
                  className='CommonTable__data-row'
                >
                  <>
                    <TableData className='CommonTable__data-item'>
                      <span>{acceptance.building || EMPTY_VALUE}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{acceptance.city || EMPTY_VALUE}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{t(`broadSign.${acceptance.acceptanceStatus}`)}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{acceptance.rejectionDetails || EMPTY_VALUE}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{EMPTY_VALUE}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{EMPTY_VALUE}</span>
                    </TableData>
                  </>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          noData
        )}
      </div>
    </div>
  )
}

export default BroadSignCreationMediaTable
